import { Controller, FieldPath, useFormContext } from 'react-hook-form';
import { Divider, Input } from '@ds';
import { FormBlockExpander } from '@lp/features/Scheduler/components';
import { CSSchedulerSettingsRulesFormType } from '@lp/features/Scheduler/SchedulerClinicalSpecialistsSettings/types';
import { getNumberInputValue, getNumberInputValueHandler } from '@lp/features/Scheduler/utils';

type RenderInputProps = {
  name: FieldPath<CSSchedulerSettingsRulesFormType>;
  classString: string;
  label?: string;
};

type CSSchedulerSettingsRulesFormProps = {
  isReadOnly: boolean;
};

export const CSSchedulerSettingsRulesForm = ({ isReadOnly }: CSSchedulerSettingsRulesFormProps) => {
  const { control } = useFormContext<CSSchedulerSettingsRulesFormType>();

  const renderInput = (inputParams: RenderInputProps[]) =>
    inputParams.map((params) => (
      <Controller
        key={params.name}
        name={params.name}
        control={control}
        render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
          <Input
            type="number"
            aria-label={params.name}
            isInvalid={invalid}
            isDisabled={isReadOnly}
            errorMessage={error?.message}
            className={params.classString}
            label={params.label || ''}
            placeholder={params.label || ''}
            value={getNumberInputValue(value as number)}
            onValueChange={getNumberInputValueHandler(onChange)}
            {...field}
          />
        )}
      />
    ));

  return (
    <div className="flex flex-col gap-5">
      <FormBlockExpander title="Individual Workload Requirements">
        <div className="flex gap-3 flex-col">
          <span className="text-base-5 text-l-m">Shifts per Period</span>
          <div className="flex items-center gap-4">
            <span className="text-m-sb max-w-[120px]">Clinical Specialist & Clinical Lead</span>
            {renderInput([
              {
                name: 'shiftPerPeriods.lead.onCall',
                classString: 'max-w-[130px]',
                label: 'On Call',
              },
              {
                name: 'shiftPerPeriods.lead.onCallOnWeekend',
                classString: 'max-w-[130px]',
                label: 'Weekend On Call',
              },
              {
                name: 'shiftPerPeriods.lead.maxAtHub',
                classString: 'max-w-[130px]',
                label: 'At Hub',
              },
              {
                name: 'shiftPerPeriods.lead.maxPhoneSupport',
                classString: 'max-w-[130px]',
                label: 'Clinical Lead',
              },
              {
                name: 'shiftPerPeriods.lead.onCallAndPhoneSupport',
                classString: 'max-w-[130px]',
                label: 'Total',
              },
            ])}
            <span className="text-s-r text-base-10">
              <b>Total:</b> the maximum number of On Call Shifts a Clinical Specialist can cover in case the Clinical
              Lead are completely absent.
            </span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-m-sb min-w-[120px]">Clinical Specialist</span>
            {renderInput([
              {
                name: 'shiftPerPeriods.specialist.onCall',
                classString: 'max-w-[130px]',
                label: 'On Call',
              },
              {
                name: 'shiftPerPeriods.specialist.maxAtHub',
                classString: 'max-w-[130px]',
                label: 'At Hub',
              },
              {
                name: 'shiftPerPeriods.specialist.onCallOnWeekend',
                classString: 'max-w-[130px]',
                label: 'Weekend On Call',
              },
            ])}
          </div>
        </div>
      </FormBlockExpander>

      <FormBlockExpander title="Individual Workload Guardrails">
        <div className="flex gap-3 flex-col">
          <span className="text-base-5 text-l-m">Weekend Off Days per Period</span>
          {renderInput([{ name: 'minimumWeekends', classString: 'max-w-[140px]' }])}
          <Divider className="my-1" />
          <span className="text-base-5 text-l-m">No-Call Days per Period</span>
          {renderInput([{ name: 'noCallDaysPerPeriod', classString: 'max-w-[140px]' }])}
          <Divider className="my-1" />
          <span className="text-base-5 text-l-m">On-Call Days In-Row</span>
          <div className="flex gap-5">
            {renderInput([
              {
                name: 'onCallInRow.hardMin',
                classString: 'max-w-[140px]',
                label: 'Hard Min.',
              },
              {
                name: 'onCallInRow.recommendedMin',
                classString: 'max-w-[140px]',
                label: 'Recommended Min.',
              },
              {
                name: 'onCallInRow.recommendedMax',
                classString: 'max-w-[140px]',
                label: 'Recommended Max.',
              },
              {
                name: 'onCallInRow.hardMax',
                classString: 'max-w-[140px]',
                label: 'Hard Max.',
              },
            ])}
          </div>
          <Divider className="my-1" />
          <span className="text-base-5 text-l-m">Vacation Days</span>
          <div className="flex items-center text-sm">
            <span>Each</span>
            {renderInput([{ name: 'vacationDeduction.q1_3.each', classString: 'max-w-[80px] mx-3' }])}
            <span>vacation/personal days taken in any given period in Q1-Q3 reduce on-call requirement by</span>
            {renderInput([{ name: 'vacationDeduction.q1_3.deduction', classString: 'max-w-[80px] mx-3' }])}
            <span>day for that period;</span>
          </div>
          <div className="flex items-center text-sm">
            <span>During Q4, the</span>
            {renderInput([{ name: 'vacationDeduction.q4.each', classString: 'max-w-[80px] mx-3' }])}
            <span>vacation/personal days reduce on-call requirement by</span>
            {renderInput([{ name: 'vacationDeduction.q4.deduction', classString: 'max-w-[80px] mx-3' }])}
            <span>day for that period.</span>
          </div>
        </div>
      </FormBlockExpander>

      <FormBlockExpander title="Group Workload Guardrails">
        <div className="flex gap-4 flex-col text-sm">
          <span>
            CSs are assigned to 2 groups: A, B and C (or A and B, depending on hub size). The groups alternate stat
            holidays on an annual basis.
          </span>
          <Divider />
          <span>
            If a CS works on a company holiday, they also must work the day before and the day after (e.g. if they work
            on Christmas Day, they must work on Dec. 24 and Dec. 26).
          </span>
          <Divider />
          <div className="flex items-center">
            <span>Each company holiday worked grants +</span>
            {renderInput([{ name: 'holidayNoCallBonus', classString: 'max-w-[85px] ml-3 mr-3' }])}
            <span>Floating Holiday (a no-call day) to be used within 30 days.</span>
          </div>
        </div>
      </FormBlockExpander>
    </div>
  );
};
